<template>
  <div class="scr-panel">
    <!-- 加载中：提取 -->
    <div v-if="sc.loading && !sc.tableData" class="scr-loading">
      <div class="scr-spinner"></div>
      <span>AI 正在提取尺码数据...</span>
    </div>

    <!-- 加载中：生成图片 -->
    <div v-else-if="sc.loading && sc.tableData" class="scr-loading">
      <div class="scr-spinner"></div>
      <span>AI 正在重绘英文尺码图...</span>
    </div>

    <!-- 已生成结果 -->
    <div v-else-if="sc.rawResult" class="scr-result">
      <div class="scr-result-title">
        {{ sc.bgFromStore ? '调整表格图位置后点下载' : '生成完成' }}
        <span v-if="sc.combining || downloading" class="scr-combining-hint">
          {{ downloading ? '准备下载...' : '正在融合...' }}
        </span>
      </div>

      <CombinePositioner
        v-if="sc.bgFromStore"
        :bg-image="sc.bgFromStore"
        :fg-image="sc.rawResult"
        :product-index="pi"
      />
      <div v-else class="scr-result-img-wrapper">
        <img :src="sc.rawResult" class="scr-result-img" />
      </div>

      <div class="scr-result-actions">
        <button
          class="scr-btn scr-btn-primary"
          :disabled="downloading || sc.combining"
          @click="downloadResult"
        >
          {{ downloading ? '下载中...' : '下载尺码图片' }}
        </button>
        <button class="scr-btn scr-btn-secondary" @click="backToEdit">返回修改表格</button>
      </div>
    </div>

    <!-- 表格编辑 -->
    <div v-else-if="sc.tableData" class="scr-table-area">
      <div class="scr-table-header">
        <span class="scr-table-title">提取结果（可直接编辑）</span>
        <button class="scr-btn scr-btn-primary" @click="workbench.generateSizeChartImage(pi)">确认数据，生成图片</button>
      </div>
      <div class="scr-table-scroll">
        <table class="scr-table">
          <thead>
            <tr>
              <th v-for="(col, ci) in sc.tableData.columns" :key="ci">
                <input v-model="sc.tableData.columns[ci]" class="scr-cell-input scr-cell-header" />
              </th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="(row, ri) in sc.tableData.data" :key="ri">
              <td v-for="(col, ci) in sc.tableData.columns" :key="ci">
                <input v-model="sc.tableData.data[ri][col]" class="scr-cell-input" />
              </td>
            </tr>
          </tbody>
        </table>
      </div>
    </div>

    <!-- 错误 -->
    <div v-else-if="sc.error && !sc.tableData" class="scr-error">{{ sc.error }}</div>

    <!-- 空状态 -->
    <div v-else class="scr-empty">
      <svg viewBox="0 0 24 24" fill="none" stroke="#d8cec4" stroke-width="1.5" width="48" height="48">
        <rect x="3" y="3" width="18" height="18" rx="2"/>
        <line x1="3" y1="9" x2="21" y2="9"/>
        <line x1="3" y1="15" x2="21" y2="15"/>
        <line x1="9" y1="3" x2="9" y2="21"/>
      </svg>
      <span>上传尺码图并点击提取开始</span>
    </div>
  </div>
</template>

<script setup>
/* eslint-disable no-undef */
import { computed, ref } from 'vue'
import { useWorkbenchStore } from '@/stores/useWorkbenchStore'
import CombinePositioner from './CombinePositioner.vue'

const props = defineProps({ productIndex: { type: Number, required: true } })
const workbench = useWorkbenchStore()
const pi = computed(() => props.productIndex)
const sc = computed(() => workbench.getSc(pi.value))

const downloading = ref(false)
const downloadResult = async () => {
  if (downloading.value || sc.value.combining) return
  if (!sc.value.rawResult) return
  downloading.value = true
  try {
    const url = await workbench.downloadSizeChart(pi.value)
    if (!url) return
    const a = document.createElement('a')
    a.href = url
    a.download = `US_Size_Chart_${pi.value + 1}.png`
    a.click()
  } finally {
    downloading.value = false
  }
}

const backToEdit = () => {
  const sc = workbench.getSc(pi.value)
  sc.rawResult = ''
  sc.result = ''
  sc.bgFromStore = ''
  sc.combining = false
}
</script>

<style scoped>
.scr-panel {
  background: var(--bg-white);
  border-radius: var(--radius-md);
  padding: 24px;
  display: flex;
  flex-direction: column;
  gap: 16px;
  min-height: 400px;
}
.scr-loading {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 16px;
  flex: 1;
  color: var(--text-body);
  font-size: 14px;
}
.scr-spinner {
  width: 36px;
  height: 36px;
  border: 3px solid var(--border-strong);
  border-top-color: var(--primary-color);
  border-radius: 50%;
  animation: spin 0.8s linear infinite;
}
@keyframes spin {
  to { transform: rotate(360deg); }
}
.scr-empty {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 12px;
  flex: 1;
  color: var(--text-sub);
  font-size: 13px;
}
.scr-error {
  color: var(--danger);
  font-size: 13px;
  padding: 8px 12px;
  background: var(--danger-soft);
  border-radius: var(--radius-sm);
}
.scr-table-area {
  display: flex;
  flex-direction: column;
  gap: 12px;
}
.scr-table-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
}
.scr-table-title {
  font-size: 14px;
  font-weight: 600;
  color: var(--text-title);
}
.scr-table-scroll {
  overflow-x: auto;
}
.scr-table {
  width: 100%;
  border-collapse: collapse;
  font-size: 13px;
}
.scr-table th,
.scr-table td {
  border: 1px solid var(--border-color);
  padding: 0;
}
.scr-cell-input {
  width: 100%;
  border: none;
  padding: 8px 10px;
  font-size: 13px;
  background: transparent;
  outline: none;
  color: var(--text-title);
  box-sizing: border-box;
}
.scr-cell-input:focus {
  background: var(--primary-softer);
}
.scr-cell-header {
  font-weight: 600;
  background: var(--surface-soft);
}
.scr-result {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 16px;
}
.scr-result-title {
  font-size: 14px;
  font-weight: 600;
  color: var(--text-title);
  display: flex;
  align-items: center;
  gap: 10px;
}
.scr-combining-hint {
  font-size: 12px;
  font-weight: 400;
  color: var(--primary-active);
}
.scr-result-img-wrapper {
  position: relative;
  width: 100%;
  display: flex;
  justify-content: center;
}
.scr-combining-overlay {
  position: absolute;
  inset: 0;
  background: rgba(255,255,255,0.7);
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 8px;
  border-radius: var(--radius-sm);
  color: var(--text-body);
  font-size: 13px;
}
.scr-result-img {
  max-width: 100%;
  max-height: 500px;
  object-fit: contain;
  border-radius: var(--radius-sm);
  border: 1px solid var(--border-color);
}
.scr-result-actions {
  display: flex;
  gap: 12px;
}
.scr-btn {
  padding: 8px 18px;
  border: none;
  border-radius: var(--radius-sm);
  font-size: 13px;
  cursor: pointer;
  transition: all var(--dur-fast) var(--ease-out);
}
.scr-btn-primary {
  background: var(--primary-gradient);
  color: #fff;
  box-shadow: var(--shadow-primary);
}
.scr-btn-primary:hover {
  box-shadow: var(--shadow-primary-hover);
  transform: translateY(-1px);
}
.scr-btn-primary:active {
  transform: translateY(0);
}
.scr-btn-secondary {
  background: var(--bg-white);
  color: var(--text-body);
  border: 1px solid var(--border-strong);
}
.scr-btn-secondary:hover {
  border-color: var(--primary-color);
  color: var(--primary-active);
  background: var(--primary-softer);
}
</style>
